import { Bugfender, LogLevel } from '@bugfender/sdk';
import { Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useState } from 'react';

import { Drawer, DrawerProps } from '@/components/Layout/Drawer';

import { getAssetPath } from '@/constants/configs';

import { useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';

import { selectUserExtraQuestions } from '@/redux/configs/selectors';
import { selectPEPQuestions } from '@/redux/user-data/selectors';

import { PEPQuestion, QuestionCode, QuestionWithAnswer } from '@/types/api/pep';
import { BugfenderTagType } from '@/types/document';
import { User } from '@/types/user';

import { QuestionAboutConnection } from './QuestionAboutConnection';
import { QuestionAboutDualCitizenship } from './QuestionAboutDualCitizenship';
import { QuestionAboutFundingSource } from './QuestionAboutFundingSource';
import { QuestionAboutMonthlyDeposit } from './QuestionAboutMonthlyDeposit';
import { QuestionAboutMonthlyIncome } from './QuestionAboutMonthlyIncome';
import { QuestionAboutMultipleFundingSources } from './QuestionAboutMultipleFundingSources';
import { QuestionAboutRelatives } from './QuestionAboutRelatives';
import { QuestionAboutTotalAnnualDeposit } from './QuestionAboutTotalAnnualDeposit';
import { QuestionAboutUSIndicia } from './QuestionAboutUSIndicia';
import { QuestionAccountPurpose } from './QuestionAccountPurpose';
import { QuestionAccountTypeOrder } from './QuestionAccountTypeOrder';
import { QuestionProps } from './QuestionDrawer.interface';
import { QuestionEXCPrimaryAccount } from './QuestionEXCPrimaryAccount';
import { QuestionRenouncedUS } from './QuestionRenouncedUS';
import { RemediationQuestionExpectedMonthlyDeposits } from './RemediationQuestionAccountType/RemediationQuestionExpectedMonthlyDeposits';
import { RemediationQuestionFundingSources } from './RemediationQuestionAccountType/RemediationQuestionFundingSources';

import {
  DualCitizenshipImage,
  QuestionsContainer,
  USIndiciaImage,
} from './QuestionDrawer.styled';

interface QuestionsDrawerProps extends DrawerProps {
  user: User;
}

export function QuestionsDrawer({
  user,
  ...props
}: Readonly<QuestionsDrawerProps>) {
  const { t } = useTranslate();
  const rawQuestions = useAppSelector(selectPEPQuestions);
  const extraQuestions = useAppSelector(selectUserExtraQuestions);
  const [questions, setQuestions] = useState<QuestionWithAnswer[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (rawQuestions && props.open) {
      const newQuestions: PEPQuestion[] = [];
      const sortedRawQuestions = [...rawQuestions]
        .filter(q => q.code !== QuestionCode.Personal)
        .sort((a, b) => a.priority - b.priority);

      sortedRawQuestions.forEach(question => {
        if (!(user.pepAnswered || []).includes(question.code)) {
          newQuestions.push(question);
        }
      });

      newQuestions.push(
        ...extraQuestions.map(item => ({
          ...item,
          question: t(item.question),
          description: item.description ? t(item.description) : '',
          tooltip: item.tooltip ? t(item.tooltip) : '',
          extraProps: { ...item.extraProps, questionKey: item.question },
        })),
      );
      Bugfender.sendLog({
        tag: BugfenderTagType.MORE_INFO,
        level: LogLevel.Info,
        text: JSON.stringify({
          action: 'Open drawer: PEP Questions',
          extraQuestions,
          newQuestions,
          user,
        }),
      });

      setQuestions(newQuestions);
      setCurrentIndex(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);

  const onNext = useCallback(() => {
    if (currentIndex >= questions.length - 1) {
      props.onClose();
    } else {
      setCurrentIndex(prev => prev + 1);
    }
  }, [currentIndex, props, questions.length]);

  const renderDualCitizenshipImage = useCallback(() => {
    const tenant = user.tenant;

    if (tenant) {
      return (
        <DualCitizenshipImage
          src={getAssetPath(
            `/dual-citizenship/question-dual-citizenship-${tenant}.svg`,
          )}
        />
      );
    }

    return <DualCitizenshipImage />;
  }, [user.tenant]);

  const renderQuestion = useCallback(() => {
    const currentQuestion = questions[currentIndex];

    if (currentQuestion) {
      const commonProps: QuestionProps = {
        index: currentIndex,
        total: questions.length,
        question: currentQuestion,
        onAnswered: onNext,
      };

      switch (currentQuestion.code) {
        case QuestionCode.Connection:
          return <QuestionAboutConnection {...commonProps} />;
        case QuestionCode.Relatives:
          return <QuestionAboutRelatives {...commonProps} />;
        case QuestionCode.USIndica:
          return (
            <QuestionAboutUSIndicia
              {...commonProps}
              image={<USIndiciaImage />}
            />
          );
        case QuestionCode.DualCitizenship:
          return (
            <QuestionAboutDualCitizenship
              {...commonProps}
              image={renderDualCitizenshipImage()}
            />
          );
        case QuestionCode.TotalAnnualDeposit:
          return <QuestionAboutTotalAnnualDeposit {...commonProps} />;
        case QuestionCode.ExpectedMonthlyDeposit:
          return <QuestionAboutMonthlyDeposit {...commonProps} />;
        case QuestionCode.GrossMonthlyIncome:
          return <QuestionAboutMonthlyIncome {...commonProps} />;
        case QuestionCode.FundingSource:
          return <QuestionAboutFundingSource {...commonProps} />;
        case QuestionCode.MultipleFundingSources:
          return <QuestionAboutMultipleFundingSources {...commonProps} />;
        case QuestionCode.RenouncedUSCitizenship:
          return <QuestionRenouncedUS {...commonProps} />;
        case QuestionCode.AccountTypeOrder:
          return <QuestionAccountTypeOrder {...commonProps} />;
        case QuestionCode.EXCPrimaryAccount:
          return <QuestionEXCPrimaryAccount {...commonProps} />;
        case QuestionCode.AccountPurpose:
          return <QuestionAccountPurpose {...commonProps} />;
        case QuestionCode.RemediationExpectedMonthlyDeposits:
          return (
            <RemediationQuestionExpectedMonthlyDeposits {...commonProps} />
          );
        case QuestionCode.RemediationFundingSources:
          return <RemediationQuestionFundingSources {...commonProps} />;
      }
    }

    return null;
  }, [currentIndex, onNext, questions, renderDualCitizenshipImage]);

  return (
    <Drawer
      {...props}
      headerBgColor={(theme: Theme) => theme.palette.background.paper}
      title={<Typography variant="h5">{t('questionDrawer.title')}</Typography>}
    >
      <QuestionsContainer>{renderQuestion()}</QuestionsContainer>
    </Drawer>
  );
}
